import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

// Phone recovery stays unavailable until a real SMS transport delivers the code.
const PHONE_AUTH_ENABLED = false;

const GENERIC_SENT_MESSAGE = "If an account matches those details, a reset code has been sent.";

export default function ForgotPassword() {
  const [mode, setMode] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState("request");
  const [busy, setBusy] = useState(false);

  const resetPayload = () => ({ [mode]: identifier.trim() });

  const requestReset = async (event) => {
    event.preventDefault();
    if (!identifier.trim()) {
      toast.error(`Please enter your ${mode}.`);
      return;
    }

    setBusy(true);
    try {
      // The response is deliberately generic to avoid revealing whether an
      // account exists. The OTP is delivered out-of-band and is never read from
      // the response or persisted in browser storage.
      await api.post("/auth/forgot-password", resetPayload());
      setStep("reset");
      toast.success(GENERIC_SENT_MESSAGE);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const resendReset = async () => {
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", resetPayload());
      toast.success(GENERIC_SENT_MESSAGE);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      toast.error("Please enter the 6-digit reset code.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        ...resetPayload(),
        otp,
        password,
      });
      setOtp("");
      setPassword("");
      setConfirmPassword("");
      setStep("done");
      toast.success("Password reset successful. You can now log in.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setIdentifier("");
  };

  const goBack = () => {
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setStep("request");
  };

  return (
    <div className="site-shell min-h-screen grid place-items-center px-6" data-testid="forgot-password-page">
      <div className="premium-card w-full max-w-md border border-border rounded-3xl p-8 bg-background">
        <Link to="/" className="mb-8 inline-flex" aria-label="ShreeHomes home">
          <BrandLogo size="sm" subtitle="Secure account recovery" />
        </Link>
        <p className="gold-eyebrow text-xs font-bold uppercase tracking-[0.25em] mb-3">Account recovery</p>

        {step === "request" && (
          <form onSubmit={requestReset} className="space-y-4">
            <h1 className="font-display text-3xl font-black text-primary">Forgot password</h1>
            <p className="text-sm text-muted-foreground">
              Choose where you want to receive your one-time reset code.
            </p>

            <div className="flex border-b border-border" data-testid="forgot-mode-toggle">
              {(PHONE_AUTH_ENABLED ? ["email", "phone"] : ["email"]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => changeMode(item)}
                  className={`flex-1 pb-2 text-xs font-semibold capitalize border-b-2 ${
                    mode === item
                      ? "text-primary border-primary font-bold"
                      : "text-muted-foreground border-transparent hover:text-foreground"
                  }`}
                  data-testid={`forgot-mode-${item}`}
                >
                  Reset via {item}
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="forgot-identifier">{mode === "email" ? "Email" : "Phone number"}</Label>
              <Input
                id="forgot-identifier"
                type={mode === "email" ? "email" : "tel"}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
                minLength={mode === "phone" ? 8 : undefined}
                autoComplete={mode === "email" ? "email" : "tel"}
                placeholder={mode === "email" ? "you@example.com" : "+919999999999"}
                data-testid={`forgot-${mode}`}
              />
            </div>

            <Button type="submit" disabled={busy} className="w-full font-bold" data-testid="forgot-request-submit">
              {busy ? "Sending code…" : "Send reset code"}
            </Button>
            <Button type="button" variant="link" asChild className="w-full text-xs text-muted-foreground">
              <Link to="/login">← Back to login</Link>
            </Button>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={resetPassword} className="space-y-4">
            <h1 className="font-display text-3xl font-black text-primary">Enter reset code</h1>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code sent to your {mode} and choose a new password.
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="forgot-otp">Reset code</Label>
              <Input
                id="forgot-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
                required
                className="text-center font-bold text-xl h-12 tracking-[0.5em]"
                data-testid="forgot-otp"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="forgot-new-password">New password</Label>
              <Input
                id="forgot-new-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                data-testid="forgot-new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="forgot-confirm-password">Confirm new password</Label>
              <Input
                id="forgot-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                data-testid="forgot-confirm-password"
              />
            </div>

            <Button type="submit" disabled={busy} className="w-full font-bold" data-testid="forgot-reset-submit">
              {busy ? "Resetting…" : "Reset password"}
            </Button>
            <div className="flex flex-col sm:flex-row justify-center gap-1 sm:gap-3">
              <Button type="button" variant="link" disabled={busy} onClick={resendReset} className="text-xs text-muted-foreground">
                Resend code
              </Button>
              <Button type="button" variant="link" disabled={busy} onClick={goBack} className="text-xs text-muted-foreground">
                Change {mode}
              </Button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-4" data-testid="forgot-password-done">
            <h1 className="font-display text-3xl font-black text-primary">Password updated</h1>
            <p className="text-sm text-muted-foreground">Your password was reset securely.</p>
            <Button asChild className="w-full">
              <Link to="/login">Back to login</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
